import {
	type ExcludedSystemModule,
	type ExcludedSystemModuleForRegistry,
	type ExternalModuleConfiguration,
	type ExternalModuleManifest,
	ExternalModuleName,
} from '../../types';

/**
 * Get the configuration for each module to be used for setting System registry.
 *
 * !!Warning!! Set isDefault to true for modules with default exports. It won't affect modules without defaults.
 * !!Warning!! require() cannot be dynamic. It needs to be a known value at build time.
 * @param {ExternalModuleName} moduleName
 * @returns {ExternalModuleConfiguration | undefined}
 */
export const getExternalModuleConfiguration = (
	moduleName: ExternalModuleName,
): ExternalModuleConfiguration | undefined => {
	try {
		switch (moduleName) {
			case ExternalModuleName.React:
				return {
					isDefault: true,
					import: require('react'),
				};
			case ExternalModuleName.ReactDOM:
				return {
					isDefault: true,
					import: require('react-dom'),
				};
			case ExternalModuleName.ReactIntlNext: {
				try {
					return {
						isDefault: true,
						import: require('react-intl-next'),
					};
				} catch {
					return {
						isDefault: true,
						/* eslint-disable import/no-extraneous-dependencies */
						import: require('react-intl'),
					};
				}
			}
			case ExternalModuleName.ReactIntl: {
				try {
					return {
						isDefault: true,
						/* eslint-disable import/no-extraneous-dependencies */
						import: require('react-intl'),
					};
				} catch {
					return {
						isDefault: true,
						import: require('react-intl-next'),
					};
				}
			}
			case ExternalModuleName.EmotionReact:
				return {
					isDefault: false,
					import: require('@emotion/react'),
				};
			case ExternalModuleName.FeatureGateJsClientPublic:
				return {
					isDefault: false,
					import: require('@atlaskit/feature-gate-js-client'),
				};
			case ExternalModuleName.AnalyticsContext:
				return {
					isDefault: true,
					import: require('@atlaskit/analytics-next-stable-react-context'),
				};
			case ExternalModuleName.AnalyticsNext:
				return {
					isDefault: false,
					import: require('@atlaskit/analytics-next'),
				};
			default:
				return;
		}
	} catch (error) {
		console.error(`Module [${moduleName}] is possibly not installed. ${error}`);
		return;
	}
};

/**
 * Get system module from the list of external modules using the configuration.
 *
 * @param {ExternalModuleManifest} module
 * @returns {ExcludedSystemModule}
 */
export const getModuleToLoad = (
	module: ExternalModuleManifest | ExcludedSystemModule,
): ExcludedSystemModule => {
	if ('module' in module) {
		return module;
	}

	const fallbackModule = {
		module: module.name,
		import: undefined,
		isDefault: false,
	};
	const isValidExternalModule = Object.values(ExternalModuleName).find(
		(moduleName) => moduleName === module.name,
	);

	if (!isValidExternalModule) {
		console.error(
			`Excluded system module [${module.name}] must be setup inside '/remote-setup/config.ts'. Modules cannot be excluded and required dynamically.`,
		);
		return fallbackModule;
	}

	const config = getExternalModuleConfiguration(module.name);

	if (!config) {
		return fallbackModule;
	}

	const excludedSystemModule = {
		module: module.name,
		...config,
	};

	return excludedSystemModule;
};

/**
 * Prepare system module to be set in System registry.
 *
 * @param {ExcludedSystemModule} entry
 * @param {string} source
 * @returns {ExcludedSystemModuleForRegistry}
 */
export const getSystemModule = (
	entry: ExcludedSystemModule,
	source: string,
): ExcludedSystemModuleForRegistry => {
	if (entry.isDefault) {
		return {
			module: entry.module,
			import: {
				...entry.import,
				default: entry.import,
				__useDefault: true,
			},
			scopedModuleId: `${source}:${entry.module}`,
		};
	}
	return {
		module: entry.module,
		import: entry.import,
		scopedModuleId: `${source}:${entry.module}`,
	};
};

/**
 * Build each module with a scope determined by each plugin.
 *
 * @param {ExcludedSystemModuleForRegistry[]} entries
 * @returns {Record<string, string>} e.g. { react: 'app-switcher:react' }
 *
 */
export const getScopedModules = (
	entries: ExcludedSystemModuleForRegistry[],
): Record<string, string> | null => {
	try {
		const scopedModules = entries.map(
			(entry) =>
				entry.import && {
					[entry.module]: entry.scopedModuleId,
				},
		);
		return Object.assign({}, ...scopedModules);
	} catch (error) {
		console.error(`Could not set scoped modules with error message: ${error}`);
		return null;
	}
};
